import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, Brain, Plus, Save, Trash2 } from 'lucide-react';
import chatAgentService from '../../chatagents/new/ChatIntents/chatAgentService';
import { ingestionService } from '../../../../components/api/ingestionService';
import {
  embeddingProviderService,
  EmbeddingProvider,
} from '../../../../components/api/embeddingProviderService';
import {
  llmConfigurationService,
  LLMConfiguration,
} from '../../../../components/api/llmConfigurationService';
import {
  tenantDefaultsService,
  getApiErrorMessage,
  NIL_GUID,
  CopilotKeyDto,
  TenantDefaultsKeyType,
} from '../../../../components/api/tenantDefaultsService';
import BaseUrlsCard from './BaseUrlsCard';
import OrchestrationKeysCard from './OrchestrationKeysCard';

type KeyTypeForm = 'Universal' | 'CoPilot';

interface CopilotOption {
  id: string;
  label: string;
}

interface CatalogOption {
  id: string;
  label: string;
}

const normalizeApiList = (res: unknown): unknown[] => {
  if (Array.isArray(res)) return res;
  if (res && typeof res === 'object') {
    const obj = res as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data;
    if (Array.isArray(obj.items)) return obj.items;
    if (Array.isArray(obj.result)) return obj.result;
  }
  return [];
};

const vectorStoreLabel = (item: Record<string, unknown>): string => {
  const description = typeof item.description === 'string' ? item.description.trim() : '';
  const collectionName =
    typeof item.collectionName === 'string' ? item.collectionName.trim() : '';
  const provider = typeof item.provider === 'string' ? item.provider.trim() : '';
  const agentTitle = typeof item.agent_title === 'string' ? item.agent_title.trim() : '';
  const id = typeof item.id === 'string' ? item.id : '';
  return description || collectionName || provider || agentTitle || id || 'Unnamed vector store';
};

const embeddingOptionLabel = (provider: EmbeddingProvider): string => {
  const display = provider.displayName?.trim() || provider.name?.trim() || 'Embedding provider';
  const model = provider.defaultModel?.trim();
  return model ? `${display} · ${model}` : display;
};

const llmOptionLabel = (llm: LLMConfiguration): string => {
  const model = llm.modelName?.trim() || 'LLM';
  const suffix = llm.isDefault ? ' (Default)' : '';
  return `${model}${suffix}`;
};

const isNilGuid = (id: string) =>
  !id || id.toLowerCase() === NIL_GUID.toLowerCase();

const normalizeKeyType = (value: string): TenantDefaultsKeyType =>
  value.toLowerCase() === 'copilot' ? 'CoPilot' : 'Universal';

const maskSecret = (value: string) => {
  if (!value) return '—';
  if (value.length <= 4) return '••••';
  return `${'•'.repeat(Math.min(value.length - 4, 12))}${value.slice(-4)}`;
};

const inputClass =
  'w-full bg-neutral-800 border border-neutral-700 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed';

const DefaultsSettings: React.FC = () => {
  const [keyType, setKeyType] = useState<KeyTypeForm>('Universal');
  const [copilotId, setCopilotId] = useState(NIL_GUID);
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const [keys, setKeys] = useState<CopilotKeyDto[]>([]);
  const [keysLoading, setKeysLoading] = useState(true);
  const [keysError, setKeysError] = useState<string | null>(null);
  const [keySaving, setKeySaving] = useState(false);
  const [keyDeletingId, setKeyDeletingId] = useState<string | null>(null);
  const [keySaveError, setKeySaveError] = useState<string | null>(null);
  const [keySaveSuccess, setKeySaveSuccess] = useState<string | null>(null);

  const [copilotOptions, setCopilotOptions] = useState<CopilotOption[]>([]);
  const [copilotsLoading, setCopilotsLoading] = useState(true);
  const [copilotsError, setCopilotsError] = useState<string | null>(null);

  const [vectorStoreId, setVectorStoreId] = useState('');
  const [embeddingProviderId, setEmbeddingProviderId] = useState('');
  const [llmId, setLlmId] = useState('');
  const [vectorOptions, setVectorOptions] = useState<CatalogOption[]>([]);
  const [embeddingOptions, setEmbeddingOptions] = useState<CatalogOption[]>([]);
  const [llmOptions, setLlmOptions] = useState<CatalogOption[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [platformSaving, setPlatformSaving] = useState(false);
  const [platformError, setPlatformError] = useState<string | null>(null);
  const [platformSuccess, setPlatformSuccess] = useState<string | null>(null);

  const resolveCopilotLabel = useCallback(
    (id: string, type: string) => {
      if (normalizeKeyType(type) === 'Universal' || isNilGuid(id)) return 'All';
      return copilotOptions.find((o) => o.id === id)?.label ?? id;
    },
    [copilotOptions]
  );

  const loadCopilotKeys = useCallback(async () => {
    setKeysLoading(true);
    setKeysError(null);
    try {
      const res = await tenantDefaultsService.getCopilotKeys();
      setKeys(Array.isArray(res) ? res : normalizeApiList(res) as CopilotKeyDto[]);
    } catch (err) {
      console.error('Failed to load copilot keys', err);
      setKeysError(getApiErrorMessage(err, 'Failed to load CoPilot API keys.'));
      setKeys([]);
    } finally {
      setKeysLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCopilots = async () => {
      setCopilotsLoading(true);
      setCopilotsError(null);
      try {
        const response = await chatAgentService.getAgentList();
        if (cancelled) return;
        const list = normalizeApiList(response?.success ? response.data : response);
        const options = list
          .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
          .map((item) => {
            const id = String(item.agent_id ?? item.id ?? '');
            const title = String(item.agent_title ?? item.title ?? item.name ?? id);
            return { id, label: title };
          })
          .filter((opt) => opt.id);
        setCopilotOptions(options);
      } catch (err) {
        console.error('Failed to load copilots', err);
        if (!cancelled) {
          setCopilotsError('Failed to load copilots.');
          setCopilotOptions([]);
        }
      } finally {
        if (!cancelled) setCopilotsLoading(false);
      }
    };

    void loadCopilots();
    void loadCopilotKeys();
    return () => {
      cancelled = true;
    };
  }, [loadCopilotKeys]);

  useEffect(() => {
    let cancelled = false;

    const loadCatalogsAndPlatform = async () => {
      setCatalogLoading(true);
      setCatalogError(null);
      setPlatformError(null);
      try {
        const [vectorRes, embeddingRes, llmRes] = await Promise.all([
          ingestionService.getVectorStores(),
          embeddingProviderService.getList(),
          llmConfigurationService.getList(),
        ]);

        if (cancelled) return;

        const vectorList = normalizeApiList(vectorRes)
          .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
          .map((item) => ({
            id: String(item.id ?? ''),
            label: vectorStoreLabel(item),
          }))
          .filter((opt) => opt.id);

        const embeddingList = normalizeApiList(embeddingRes)
          .filter((item): item is EmbeddingProvider => {
            if (!item || typeof item !== 'object') return false;
            const provider = item as EmbeddingProvider;
            return Boolean(provider.id) && provider.isActive !== false;
          })
          .map((provider) => ({
            id: provider.id,
            label: embeddingOptionLabel(provider),
          }));

        const llmList = normalizeApiList(llmRes)
          .filter((item): item is LLMConfiguration => {
            if (!item || typeof item !== 'object') return false;
            const llm = item as LLMConfiguration;
            return Boolean(llm.id) && llm.isActive !== false;
          })
          .map((llm) => ({
            id: llm.id,
            label: llmOptionLabel(llm),
          }));

        setVectorOptions(vectorList);
        setEmbeddingOptions(embeddingList);
        setLlmOptions(llmList);
      } catch (err) {
        console.error('Failed to load platform default catalogs', err);
        if (!cancelled) {
          setCatalogError(
            getApiErrorMessage(err, 'Failed to load vector stores, embedding providers, or LLMs.')
          );
          setVectorOptions([]);
          setEmbeddingOptions([]);
          setLlmOptions([]);
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }

      try {
        const platformRes = await tenantDefaultsService.getPlatformDefaults();
        if (cancelled || !platformRes) return;
        setVectorStoreId(platformRes.defaultVectorStore ?? '');
        setEmbeddingProviderId(platformRes.defaultEmbeddingProvider ?? '');
        setLlmId(platformRes.defaultLlm ?? '');
      } catch (err) {
        console.error('Failed to load platform defaults', err);
        if (!cancelled) {
          setPlatformError(getApiErrorMessage(err, 'Failed to load platform defaults.'));
        }
      }
    };

    void loadCatalogsAndPlatform();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleKeyTypeChange = (next: KeyTypeForm) => {
    setKeyType(next);
    setKeySaveError(null);
    setKeySaveSuccess(null);
    if (next === 'Universal') {
      setCopilotId(NIL_GUID);
    } else {
      setCopilotId('');
    }
  };

  const canAddKey =
    Boolean(apiKey.trim()) &&
    (keyType === 'Universal' || Boolean(copilotId));

  const handleAddKey = async () => {
    if (!canAddKey || keySaving) return;
    setKeySaving(true);
    setKeySaveError(null);
    setKeySaveSuccess(null);
    try {
      await tenantDefaultsService.upsertCopilotKey({
        keyType,
        copilotId: keyType === 'Universal' ? NIL_GUID : copilotId,
        key: apiKey.trim(),
        secret: secret.trim(),
        isActive: true,
      });
      setApiKey('');
      setSecret('');
      if (keyType === 'CoPilot') setCopilotId('');
      setKeySaveSuccess('CoPilot API key saved.');
      await loadCopilotKeys();
    } catch (err) {
      console.error('Failed to upsert copilot key', err);
      setKeySaveError(getApiErrorMessage(err, 'Failed to save CoPilot API key.'));
    } finally {
      setKeySaving(false);
    }
  };

  const handleDeleteKey = async (row: CopilotKeyDto) => {
    if (keyDeletingId) return;
    const label = resolveCopilotLabel(row.copilotId, row.keyType);
    const confirmed = window.confirm(
      `Delete this ${normalizeKeyType(row.keyType)} CoPilot API key for "${label}"? This cannot be undone.`
    );
    if (!confirmed) return;

    setKeyDeletingId(row.id);
    setKeySaveError(null);
    setKeySaveSuccess(null);
    try {
      await tenantDefaultsService.deleteCopilotKey(row.id);
      setKeySaveSuccess('CoPilot API key deleted.');
      await loadCopilotKeys();
    } catch (err) {
      console.error('Failed to delete copilot key', err);
      setKeySaveError(getApiErrorMessage(err, 'Failed to delete CoPilot API key.'));
    } finally {
      setKeyDeletingId(null);
    }
  };

  const handleSavePlatform = async () => {
    if (platformSaving) return;
    setPlatformSaving(true);
    setPlatformError(null);
    setPlatformSuccess(null);
    try {
      await tenantDefaultsService.upsertPlatformDefaults({
        defaultVectorStore: vectorStoreId,
        defaultLlm: llmId,
        defaultEmbeddingProvider: embeddingProviderId,
      });
      setPlatformSuccess('Platform defaults saved.');
    } catch (err) {
      console.error('Failed to save platform defaults', err);
      setPlatformError(getApiErrorMessage(err, 'Failed to save platform defaults.'));
    } finally {
      setPlatformSaving(false);
    }
  };

  return (
    <div className="space-y-8 text-sm">
      {/* CoPilot API Keys */}
      <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-5">
        <div className="flex items-center gap-2 mb-4 text-gray-400">
          <KeyRound className="w-4 h-4" />
          <h3 className="font-medium text-white text-base">CoPilot API Keys</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-gray-400 text-xs">Key Type</span>
            <select
              value={keyType}
              onChange={(e) => handleKeyTypeChange(e.target.value as KeyTypeForm)}
              className={inputClass}
              aria-label="Key type"
            >
              <option value="Universal">Universal</option>
              <option value="CoPilot">CoPilot</option>
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-gray-400 text-xs">CoPilot</span>
            <select
              value={keyType === 'Universal' ? NIL_GUID : copilotId}
              onChange={(e) => setCopilotId(e.target.value)}
              disabled={keyType === 'Universal' || copilotsLoading}
              className={inputClass}
              aria-label="CoPilot"
            >
              {keyType === 'Universal' ? (
                <option value={NIL_GUID}>All</option>
              ) : copilotsLoading ? (
                <option value="">Loading…</option>
              ) : copilotOptions.length === 0 ? (
                <option value="">No copilots found</option>
              ) : (
                <>
                  <option value="">Select CoPilot…</option>
                  {copilotOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </>
              )}
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-gray-400 text-xs">Key</span>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="API key"
              className={inputClass}
              aria-label="API key"
              autoComplete="off"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-gray-400 text-xs">Secret</span>
            <input
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder="Secret (optional)"
              className={inputClass}
              aria-label="Secret"
              autoComplete="off"
            />
          </label>
        </div>

        {(copilotsError || keysError || keySaveError) && (
          <p className="text-xs text-rose-400 mb-3">
            {keySaveError || keysError || copilotsError}
          </p>
        )}
        {keySaveSuccess && (
          <p className="text-xs text-emerald-400 mb-3">{keySaveSuccess}</p>
        )}

        <button
          type="button"
          onClick={() => void handleAddKey()}
          disabled={!canAddKey || keySaving}
          className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
          {keySaving ? 'Saving…' : 'Add key'}
        </button>

        {keysLoading ? (
          <p className="mt-5 text-xs text-gray-400">Loading keys…</p>
        ) : keys.length > 0 ? (
          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-neutral-800">
                  <th className="py-2 pr-3 font-medium">Key Type</th>
                  <th className="py-2 pr-3 font-medium">CoPilot</th>
                  <th className="py-2 pr-3 font-medium">Key</th>
                  <th className="py-2 pr-3 font-medium">Secret</th>
                  <th className="py-2 pr-3 font-medium">Active</th>
                  <th className="py-2 font-medium w-10" />
                </tr>
              </thead>
              <tbody>
                {keys.map((row) => (
                  <tr key={row.id} className="border-b border-neutral-800/60 text-gray-300">
                    <td className="py-2.5 pr-3">{normalizeKeyType(row.keyType)}</td>
                    <td className="py-2.5 pr-3">
                      {resolveCopilotLabel(row.copilotId, row.keyType)}
                    </td>
                    <td className="py-2.5 pr-3 font-mono text-xs">{maskSecret(row.key)}</td>
                    <td className="py-2.5 pr-3 font-mono text-xs">{maskSecret(row.secret)}</td>
                    <td className="py-2.5 pr-3">{row.isActive ? 'Yes' : 'No'}</td>
                    <td className="py-2.5">
                      <button
                        type="button"
                        onClick={() => void handleDeleteKey(row)}
                        disabled={keyDeletingId === row.id}
                        className="text-gray-400 hover:text-red-400 disabled:opacity-50 transition-colors"
                        aria-label={`Delete ${normalizeKeyType(row.keyType)} key for ${resolveCopilotLabel(row.copilotId, row.keyType)}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mt-5 text-xs text-gray-500">No CoPilot API keys configured yet.</p>
        )}
      </div>

      <OrchestrationKeysCard />

      <BaseUrlsCard />

      {/* Platform Defaults */}
      <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-5">
        <div className="flex items-center gap-2 mb-4 text-gray-400">
          <Brain className="w-4 h-4" />
          <h3 className="font-medium text-white text-base">Platform Defaults</h3>
        </div>

        <div className="space-y-4">
          <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span className="text-gray-300">Default Vector Store</span>
            <select
              value={vectorStoreId}
              onChange={(e) => setVectorStoreId(e.target.value)}
              disabled={catalogLoading || vectorOptions.length === 0}
              className={`sm:w-72 ${inputClass}`}
              aria-label="Default vector store"
            >
              {catalogLoading ? (
                <option value="">Loading…</option>
              ) : vectorOptions.length === 0 ? (
                <option value="">No vector stores found</option>
              ) : (
                <>
                  <option value="">Select vector store…</option>
                  {vectorOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </>
              )}
            </select>
          </label>

          <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span className="text-gray-300">Default Embedding Provider</span>
            <select
              value={embeddingProviderId}
              onChange={(e) => setEmbeddingProviderId(e.target.value)}
              disabled={catalogLoading || embeddingOptions.length === 0}
              className={`sm:w-72 ${inputClass}`}
              aria-label="Default embedding provider"
            >
              {catalogLoading ? (
                <option value="">Loading…</option>
              ) : embeddingOptions.length === 0 ? (
                <option value="">No embedding providers found</option>
              ) : (
                <>
                  <option value="">Select embedding provider…</option>
                  {embeddingOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </>
              )}
            </select>
          </label>

          <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span className="text-gray-300">Default LLM</span>
            <select
              value={llmId}
              onChange={(e) => setLlmId(e.target.value)}
              disabled={catalogLoading || llmOptions.length === 0}
              className={`sm:w-72 ${inputClass}`}
              aria-label="Default LLM"
            >
              {catalogLoading ? (
                <option value="">Loading…</option>
              ) : llmOptions.length === 0 ? (
                <option value="">No LLMs found</option>
              ) : (
                <>
                  <option value="">Select LLM…</option>
                  {llmOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </>
              )}
            </select>
          </label>
        </div>

        {(catalogError || platformError) && (
          <p className="mt-3 text-xs text-rose-400">{platformError || catalogError}</p>
        )}
        {platformSuccess && (
          <p className="mt-3 text-xs text-emerald-400">{platformSuccess}</p>
        )}

        <button
          type="button"
          onClick={() => void handleSavePlatform()}
          disabled={platformSaving || catalogLoading}
          className="mt-4 inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm transition-colors"
        >
          <Save className="w-4 h-4" />
          {platformSaving ? 'Saving…' : 'Save defaults'}
        </button>
      </div>
    </div>
  );
};

export default DefaultsSettings;
